import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Clock, Utensils, Activity, ArrowRight, Download, ShieldCheck, AlertCircle, TrendingDown, Zap, Coffee } from 'lucide-react';
// Modal component for Privacy and Terms
function LegalModal({
  isOpen,
  onClose,
  type
}: {
  isOpen: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms';
}) {
  const content = {
    privacy: {
      title: 'Política de Privacidad',
      sections: [{
        heading: 'Información que Recopilamos',
        text: 'Recopilamos información que nos proporcionas directamente cuando realizas una compra, como tu nombre y dirección de correo electrónico. Esta información se utiliza únicamente para procesar tu pedido y enviarte el producto digital.'
      }, {
        heading: 'Uso de la Información',
        text: 'Utilizamos tu información personal para: procesar y completar tu compra, enviarte el PDF descargable, y comunicarnos contigo sobre tu pedido si es necesario.'
      }, {
        heading: 'Protección de Datos',
        text: 'Implementamos medidas de seguridad apropiadas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
      }, {
        heading: 'No Compartimos tu Información',
        text: 'No vendemos, intercambiamos ni transferimos tu información personal a terceros. Esto no incluye terceros de confianza que nos ayudan a operar nuestro sitio web o procesar pagos, siempre que esas partes acuerden mantener esta información confidencial.'
      }, {
        heading: 'Cookies',
        text: 'Nuestro sitio puede utilizar cookies para mejorar tu experiencia. Puedes elegir que tu computadora te avise cada vez que se envía una cookie, o puedes optar por desactivar todas las cookies.'
      }]
    },
    terms: {
      title: 'Términos y Condiciones',
      sections: [{
        heading: 'Producto Digital',
        text: 'Al realizar una compra, recibirás acceso inmediato a un archivo PDF descargable. Este es un producto digital y no se realizan envíos físicos.'
      }, {
        heading: 'Uso Personal',
        text: 'El contenido del PDF es para uso personal únicamente. No está permitido redistribuir, revender o compartir el contenido con terceros sin autorización expresa.'
      }, {
        heading: 'Descargo de Responsabilidad',
        text: 'La información proporcionada en este producto es solo para fines educativos e informativos. No sustituye el consejo médico profesional. Consulta a un profesional de la salud antes de comenzar cualquier programa de nutrición o ejercicio.'
      }, {
        heading: 'Política de Reembolso',
        text: 'Debido a la naturaleza digital del producto, todas las ventas son finales. Sin embargo, si tienes algún problema con tu compra, contáctanos y haremos lo posible por ayudarte.'
      }, {
        heading: 'Resultados',
        text: 'Los resultados pueden variar de persona a persona. No garantizamos resultados específicos ya que dependen de múltiples factores individuales como adherencia al plan, condiciones de salud preexistentes y otros factores de estilo de vida.'
      }, {
        heading: 'Propiedad Intelectual',
        text: 'Todo el contenido, incluyendo textos, gráficos y diseño, está protegido por derechos de autor. Queda prohibida la reproducción sin autorización.'
      }]
    }
  };
  const currentContent = content[type];
  return <AnimatePresence data-id="element-1">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-2" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-2xl md:w-full md:max-h-[80vh] bg-[#FDF8F3] rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col" data-id="element-3">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#2D3E50]/10" data-id="element-4">
              <h2 className="text-2xl font-bold text-[#2D3E50]" data-id="element-5">
                {currentContent.title}
              </h2>
              <button onClick={onClose} className="w-10 h-10 rounded-full bg-[#2D3E50]/5 hover:bg-[#2D3E50]/10 flex items-center justify-center transition-colors" data-id="element-6">
                <X className="w-5 h-5 text-[#2D3E50]" data-id="element-7" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-8">
              {currentContent.sections.map((section, index) => <div key={index} data-id="element-9">
                  <h3 className="text-lg font-semibold text-[#2D3E50] mb-2" data-id="element-10">
                    {section.heading}
                  </h3>
                  <p className="text-[#2D3E50]/70 leading-relaxed" data-id="element-11">
                    {section.text}
                  </p>
                </div>)}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-[#2D3E50]/10" data-id="element-12">
              <button onClick={onClose} className="w-full bg-[#2D3E50] hover:bg-[#3d5066] text-white py-3 rounded-lg font-semibold transition-colors" data-id="element-13">
                Entendido
              </button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
// Animation variants
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function SalesPage() {
  const [modalOpen, setModalOpen] = useState<'privacy' | 'terms' | null>(null);
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-[#FDF8F3] text-[#2D3E50] font-sans selection:bg-[#E8734A] selection:text-white" data-id="element-14">
      {/* Legal Modals */}
      <LegalModal isOpen={modalOpen === 'privacy'} onClose={() => setModalOpen(null)} type="privacy" data-id="element-15" />
      <LegalModal isOpen={modalOpen === 'terms'} onClose={() => setModalOpen(null)} type="terms" data-id="element-16" />

      {/* Sticky Header */}
      <header className="sticky top-0 z-50 bg-[#FDF8F3]/90 backdrop-blur-md border-b border-[#2D3E50]/10 py-3 px-4 md:px-8" data-id="element-17">
        <div className="max-w-6xl mx-auto flex justify-between items-center" data-id="element-18">
          <div className="font-bold text-lg md:text-xl tracking-tight text-[#2D3E50]" data-id="element-19">
            Método 3R
          </div>
          <button onClick={scrollToOffer} className="bg-[#E8734A] hover:bg-[#d66239] text-white px-4 py-2 rounded-full text-sm font-semibold transition-colors shadow-sm" data-id="element-20">
            Descargar Plan
          </button>
        </div>
      </header>

      {/* 1. HERO SECTION */}
      <section className="relative pt-12 pb-20 px-4 md:px-8 overflow-hidden" data-id="element-21">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-22">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-6" data-id="element-23">
            <motion.div variants={fadeIn} className="inline-block px-3 py-1 bg-[#5FBFB3]/10 text-[#2D3E50] rounded-full text-sm font-medium border border-[#5FBFB3]/20" data-id="element-24">
              Para hombres y mujeres de 35+
            </motion.div>

            <motion.h1 variants={fadeIn} className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-[#2D3E50]" data-id="element-25">
              Baja la panza <span className="text-[#E8734A]" data-id="element-26">sin cardio</span>
            </motion.h1>

            <motion.p variants={fadeIn} className="text-xl md:text-2xl text-[#2D3E50]/80 font-medium" data-id="element-27">
              21 días. 10 minutos. Cero saltos.
            </motion.p>

            <motion.div variants={fadeIn} className="pt-4 flex flex-col sm:flex-row gap-4" data-id="element-28">
              <button onClick={scrollToOffer} className="bg-[#E8734A] hover:bg-[#d66239] text-white text-lg px-8 py-4 rounded-lg font-bold shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2" data-id="element-29">
                Descarga el plan
                <ArrowRight className="w-5 h-5" data-id="element-30" />
              </button>
            </motion.div>

            <motion.div variants={fadeIn} className="flex items-center gap-2 text-sm text-[#2D3E50]/70" data-id="element-31">
              <ShieldCheck className="w-4 h-4 text-[#5FBFB3]" data-id="element-32" />
              <span data-id="element-33">PDF descargable • Acceso inmediato</span>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-34">
            <div className="absolute inset-0 bg-[#5FBFB3]/20 rounded-3xl transform rotate-3 scale-105 blur-xl" data-id="element-35"></div>
            <img src="https://cdn.magicpatterns.com/uploads/rK8iX3Z3ZQu5aCrgtoQWey/heylloyd-image-1770019017082.png" alt="Baja la Panza Sin Cardio PDF en tablet" className="relative rounded-2xl shadow-2xl w-full max-w-md mx-auto md:max-w-full border-4 border-white" data-id="element-36" />
          </motion.div>
        </div>
      </section>

      {/* 2. IDENTIFICATION SECTION */}
      <section className="py-16 px-4 md:px-8 bg-white" data-id="element-37">
        <div className="max-w-3xl mx-auto" data-id="element-38">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-10" data-id="element-39">
            <h2 className="text-2xl md:text-3xl font-bold text-[#2D3E50]" data-id="element-40">
              Este plan es para ti si...
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="space-y-4" data-id="element-41">
            {['La báscula sube y baja, pero la cintura no cambia', 'El cardio te deja muerto/a o adolorido/a y lo abandonas', 'Aguantas todo el día… y en la noche te da el ataque', "Ya no quieres otra dieta 'perfecta' que dura 4 días"].map((item, index) => <motion.div key={index} variants={fadeIn} className="flex items-start gap-4 p-4 rounded-xl bg-[#FDF8F3] border border-[#2D3E50]/5" data-id="element-42">
                <div className="bg-[#E8734A]/10 p-2 rounded-full shrink-0 mt-0.5" data-id="element-43">
                  <Check className="w-5 h-5 text-[#E8734A]" data-id="element-44" />
                </div>
                <p className="text-lg text-[#2D3E50]" data-id="element-45">{item}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 3. EMOTIONAL OPENING */}
      <section className="py-20 px-4 md:px-8 bg-[#2D3E50] text-[#FDF8F3]" data-id="element-46">
        <div className="max-w-3xl mx-auto text-center" data-id="element-47">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-48">
            <p className="text-xl md:text-2xl italic leading-relaxed opacity-90 mb-8" data-id="element-49">
              "Te ves de lado en el espejo y piensas: '¿Cómo puede ser que yo
              haga esfuerzo… y esta panza siga aquí?'"
            </p>
            <div className="w-16 h-1 bg-[#E8734A] mx-auto mb-8 rounded-full" data-id="element-50"></div>
            <p className="text-lg md:text-xl font-medium" data-id="element-51">
              No es que te falte disciplina. <br className="hidden md:block" data-id="element-52" />
              Es que estás usando la herramienta equivocada para un cuerpo 35+.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 4. THE TRUTH SECTION */}
      <section className="py-20 px-4 md:px-8 bg-[#FDF8F3]" data-id="element-53">
        <div className="max-w-4xl mx-auto" data-id="element-54">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-12" data-id="element-55">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2D3E50] mb-4" data-id="element-56">
              La verdad que nadie dice
            </h2>
            <p className="text-lg text-[#2D3E50]/70 max-w-2xl mx-auto" data-id="element-57">
              A los 35+, el problema no suele ser "no te mueves". El problema es
              que tu día se volvió una montaña rusa.
            </p>
          </motion.div>

          <div className="relative" data-id="element-58">
            {/* Visual representation of the cycle */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-[#2D3E50]/10 -translate-x-1/2 hidden md:block" data-id="element-59"></div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} className="space-y-8 relative" data-id="element-60">
              {[{
              icon: Coffee,
              title: 'Mañana',
              text: 'Desayuno rápido (carbo solo) → hambre temprano'
            }, {
              icon: Zap,
              title: 'Tarde',
              text: 'Estrés → café/antojos → más picos de glucosa'
            }, {
              icon: AlertCircle,
              title: 'Noche',
              text: 'Llegas con hambre acumulada → picoteo descontrolado'
            }, {
              icon: TrendingDown,
              title: 'Resultado',
              text: "Tu cuerpo guarda energía en el lugar más 'fácil': la cintura"
            }].map((item, index) => <motion.div key={index} variants={fadeIn} className="flex flex-col md:flex-row items-center gap-6 md:gap-12" data-id="element-61">
                  <div className="flex-1 md:text-right md:order-1 order-2" data-id="element-62">
                    {index % 2 === 0 ? <div data-id="element-63">
                        <h3 className="text-xl font-bold text-[#2D3E50]" data-id="element-64">
                          {item.title}
                        </h3>
                        <p className="text-[#2D3E50]/80" data-id="element-65">{item.text}</p>
                      </div> : null}
                  </div>

                  <div className="relative z-10 flex items-center justify-center w-12 h-12 rounded-full bg-[#5FBFB3] text-white shadow-lg shrink-0 md:order-2 order-1" data-id="element-66">
                    <item.icon className="w-6 h-6" data-id="element-67" />
                  </div>

                  <div className="flex-1 md:order-3 order-2" data-id="element-68">
                    {index % 2 !== 0 ? <div data-id="element-69">
                        <h3 className="text-xl font-bold text-[#2D3E50]" data-id="element-70">
                          {item.title}
                        </h3>
                        <p className="text-[#2D3E50]/80" data-id="element-71">{item.text}</p>
                      </div> : null}
                    {/* Mobile only text for even items */}
                    <div className="md:hidden" data-id="element-72">
                      {index % 2 === 0 ? <div data-id="element-73">
                          <h3 className="text-xl font-bold text-[#2D3E50]" data-id="element-74">
                            {item.title}
                          </h3>
                          <p className="text-[#2D3E50]/80" data-id="element-75">{item.text}</p>
                        </div> : null}
                    </div>
                  </div>
                </motion.div>)}
            </motion.div>
          </div>
        </div>
      </section>

      {/* 5. WHY OLD SOLUTIONS FAIL */}
      <section className="py-20 px-4 md:px-8 bg-white" data-id="element-76">
        <div className="max-w-6xl mx-auto" data-id="element-77">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-12" data-id="element-78">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2D3E50] mb-4" data-id="element-79">
              Por qué lo de siempre falla
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8" data-id="element-80">
            {[{
            title: 'Dietas extremas',
            result: 'Hambre y rebote',
            desc: 'Funcionan… hasta que el hambre gana.'
          }, {
            title: 'Cardio como castigo',
            result: 'Dolor y abandono',
            desc: 'Te cansa, te lesiona, te quita ganas.'
          }, {
            title: 'Comer poquito',
            result: 'Ansiedad nocturna',
            desc: 'Te deja sin saciedad, y la noche te cobra.'
          }].map((card, index) => <motion.div key={index} variants={fadeIn} className="bg-[#FDF8F3] p-8 rounded-2xl border border-[#2D3E50]/5 hover:shadow-lg transition-shadow" data-id="element-81">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mb-4" data-id="element-82">
                  <X className="w-6 h-6 text-red-500" data-id="element-83" />
                </div>
                <h3 className="text-xl font-bold text-[#2D3E50] mb-2" data-id="element-84">
                  {card.title}
                </h3>
                <div className="text-[#E8734A] font-semibold mb-2" data-id="element-85">
                  → {card.result}
                </div>
                <p className="text-[#2D3E50]/70" data-id="element-86">{card.desc}</p>
              </motion.div>)}
          </motion.div>

          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mt-12 text-xl font-medium text-[#2D3E50]" data-id="element-87">
            No es falta de voluntad. Es mala estrategia.
          </motion.p>
        </div>
      </section>

      {/* 6. THE 3R MECHANISM */}
      <section className="py-20 px-4 md:px-8 bg-[#2D3E50] text-white overflow-hidden relative" data-id="element-88">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#5FBFB3]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-89"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#E8734A]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-90"></div>

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-91">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-92">
            <span className="text-[#5FBFB3] font-bold tracking-wider uppercase text-sm" data-id="element-93">
              El Secreto
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mt-2 mb-4" data-id="element-94">
              La Ventana de Glucosa 3R
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-95">
              Un sistema simple para bajar cintura sin depender del cardio.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8" data-id="element-96">
            {[{
            r: 'R #1',
            title: 'Reduce picos',
            desc: 'Aprendes a comer con un orden y combinaciones que bajan antojos.',
            icon: TrendingDown,
            color: 'bg-[#5FBFB3]'
          }, {
            r: 'R #2',
            title: 'Reparte proteína',
            desc: "Metes 'anclas' fáciles que te mantienen lleno/a y estable.",
            icon: Utensils,
            color: 'bg-[#E8734A]'
          }, {
            r: 'R #3',
            title: 'Ritual post-comida',
            desc: 'El interruptor olvidado: 10 min de movimiento suave, sin dolor.',
            icon: Activity,
            color: 'bg-[#5FBFB3]'
          }].map((item, index) => <motion.div key={index} variants={fadeIn} className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-colors" data-id="element-97">
                <div className={`${item.color} w-12 h-12 rounded-xl flex items-center justify-center mb-6 text-white shadow-lg`} data-id="element-98">
                  <item.icon className="w-6 h-6" data-id="element-99" />
                </div>
                <div className="text-sm font-bold text-gray-400 mb-2" data-id="element-100">
                  {item.r}
                </div>
                <h3 className="text-2xl font-bold mb-3" data-id="element-101">{item.title}</h3>
                <p className="text-gray-300 leading-relaxed" data-id="element-102">{item.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 7. WHAT'S INCLUDED */}
      <section className="py-20 px-4 md:px-8 bg-[#FDF8F3]" data-id="element-103">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-104">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="order-2 md:order-1" data-id="element-105">
            <img src="https://cdn.magicpatterns.com/uploads/rK8iX3Z3ZQu5aCrgtoQWey/heylloyd-image-1770019017082.png" alt="Contenido del PDF" className="rounded-2xl shadow-xl w-full max-w-sm mx-auto transform rotate-[-2deg] border-4 border-white" data-id="element-106" />
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="order-1 md:order-2" data-id="element-107">
            <motion.h2 variants={fadeIn} className="text-3xl md:text-4xl font-bold text-[#2D3E50] mb-8" data-id="element-108">
              Qué incluye el PDF
            </motion.h2>

            <div className="space-y-6" data-id="element-109">
              {['Plan completo de 21 días (semana 1, 2 y 3)', 'Checklist diario 3R (para imprimir)', 'Plantillas de comidas mixtas (latinas y americanas)', 'Protocolo anti-picoteo nocturno', 'Ajustes si te estancas (sin volverte loco/a)'].map((item, index) => <motion.div key={index} variants={fadeIn} className="flex items-center gap-4" data-id="element-110">
                  <div className="w-8 h-8 rounded-full bg-[#E8734A]/10 flex items-center justify-center shrink-0" data-id="element-111">
                    <Check className="w-5 h-5 text-[#E8734A]" data-id="element-112" />
                  </div>
                  <span className="text-lg text-[#2D3E50] font-medium" data-id="element-113">
                    {item}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 8. BENEFITS/RESULTS */}
      <section className="py-20 px-4 md:px-8 bg-white" data-id="element-114">
        <div className="max-w-6xl mx-auto" data-id="element-115">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-12" data-id="element-116">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2D3E50]" data-id="element-117">
              Lo que la mayoría nota
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-118">
            {[{
            text: 'Menos hambre descontrolada en la noche',
            icon: Coffee
          }, {
            text: 'Menos hinchazón / abdomen menos inflado',
            icon: TrendingDown
          }, {
            text: 'Más sensación de control (sin obsesión)',
            icon: ShieldCheck
          }, {
            text: 'Rutina sostenible: 10 minutos post-comida',
            icon: Clock
          }].map((item, index) => <motion.div key={index} variants={fadeIn} className="bg-[#FDF8F3] p-6 rounded-xl border border-[#2D3E50]/5 text-center hover:bg-[#E8734A]/5 transition-colors" data-id="element-119">
                <div className="w-12 h-12 mx-auto bg-white rounded-full shadow-sm flex items-center justify-center mb-4 text-[#5FBFB3]" data-id="element-120">
                  <item.icon className="w-6 h-6" data-id="element-121" />
                </div>
                <p className="text-[#2D3E50] font-medium" data-id="element-122">{item.text}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 9. WHO IT'S NOT FOR */}
      <section className="py-16 px-4 md:px-8 bg-[#2D3E50]/5" data-id="element-123">
        <div className="max-w-3xl mx-auto bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-[#2D3E50]/5" data-id="element-124">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-8" data-id="element-125">
            <h2 className="text-2xl md:text-3xl font-bold text-[#2D3E50]" data-id="element-126">
              No es para ti si buscas...
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="space-y-4 mb-8" data-id="element-127">
            {['Bajar 10 kilos en una semana', 'Un plan de gimnasio intenso', 'Una dieta de prohibiciones eternas'].map((item, index) => <motion.div key={index} variants={fadeIn} className="flex items-center gap-4 justify-center" data-id="element-128">
                <X className="w-5 h-5 text-gray-400" data-id="element-129" />
                <span className="text-gray-500 line-through decoration-gray-400" data-id="element-130">
                  {item}
                </span>
              </motion.div>)}
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center pt-8 border-t border-gray-100" data-id="element-131">
            <p className="text-xl font-medium text-[#2D3E50]" data-id="element-132">
              Esto es para gente que quiere: <br className="md:hidden" data-id="element-133" />
              <span className="text-[#E8734A] font-bold" data-id="element-134">
                resultado + paz + consistencia.
              </span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* 10. FINAL CTA SECTION */}
      <section id="offer" className="py-20 px-4 md:px-8 bg-[#2D3E50] text-white" data-id="element-135">
        <div className="max-w-4xl mx-auto text-center" data-id="element-136">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-16" data-id="element-137">
            <motion.h2 variants={fadeIn} className="text-3xl md:text-5xl font-bold mb-4" data-id="element-138">
              Baja la Panza Sin Cardio (35+)
            </motion.h2>
            <motion.p variants={fadeIn} className="text-xl text-gray-300 mb-8" data-id="element-139">
              Plan de 21 días + mini-explicación + plantillas + checklists.
            </motion.p>

            <motion.div variants={fadeIn} className="flex flex-col items-center justify-center mb-8" data-id="element-140">
              <div className="flex items-center gap-3 mb-2" data-id="element-141">
                <span className="text-2xl text-gray-400 line-through" data-id="element-142">$27</span>
                <span className="bg-[#E8734A] text-white text-sm font-bold px-3 py-1 rounded-full" data-id="element-143">
                  -76% OFF
                </span>
              </div>
              <div className="flex items-baseline gap-2" data-id="element-144">
                <span className="text-6xl font-bold text-[#5FBFB3]" data-id="element-145">$6.36</span>
                <span className="text-gray-400 text-lg" data-id="element-146">USD</span>
              </div>
            </motion.div>

            <motion.button variants={fadeIn} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => window.location.href = 'https://salunza.com/cart/43797369979015:1'} className="w-full md:w-auto bg-[#E8734A] hover:bg-[#d66239] text-white text-xl px-12 py-5 rounded-xl font-bold shadow-xl transition-all mb-6 flex items-center justify-center gap-3 mx-auto" data-id="element-147">
              <Download className="w-6 h-6" data-id="element-148" />
              Descargar Ahora
            </motion.button>

            <motion.div variants={fadeIn} className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-sm text-gray-400" data-id="element-149">
              <span className="flex items-center gap-2" data-id="element-150">
                <ShieldCheck className="w-4 h-4" data-id="element-151" /> Compra única
              </span>
              <span className="flex items-center gap-2" data-id="element-152">
                <Download className="w-4 h-4" data-id="element-153" /> PDF descargable
              </span>
              <span className="flex items-center gap-2" data-id="element-154">
                <Zap className="w-4 h-4" data-id="element-155" /> Acceso inmediato
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 11. FOOTER */}
      <footer className="py-12 px-4 bg-[#2D3E50] border-t border-white/10 text-center text-gray-500 text-sm" data-id="element-156">
        <div className="flex items-center justify-center gap-6 mb-4" data-id="element-157">
          <button onClick={() => setModalOpen('privacy')} className="text-gray-400 hover:text-white transition-colors underline-offset-4 hover:underline" data-id="element-158">
            Privacy
          </button>
          <span className="text-gray-600" data-id="element-159">•</span>
          <button onClick={() => setModalOpen('terms')} className="text-gray-400 hover:text-white transition-colors underline-offset-4 hover:underline" data-id="element-160">
            Terms
          </button>
        </div>
        <p className="mb-2" data-id="element-161">
          © {new Date().getFullYear()} Método 3R. Todos los derechos
          reservados.
        </p>
        <p data-id="element-162">Hecho para gente real con vida real.</p>
      </footer>
    </div>;
}